import { Component, Input, OnChanges } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import {
  CartridgeConfigPort,
  CartridgeType,
  FuelcellCartridgeData,
  FuelcellChannelAddress,
  FuelcellPortLevel,
  cartridgeTypeLabels
} from '../../../shared/interfaces/fuelcell-data.interface';
import { FuelcellExpectation } from '../../../shared/functions/fuelcell-expectation';
import { FuelcellService } from '../../../shared/services/fuelcell.service';

/** Ports if the fuel cell has not reported any cartridge data yet (previous behaviour) */
const FALLBACK_PORTS = [1, 2];
const MAX_CAPACITY = 255;

/** Editable cartridge configuration of one port */
interface CartridgeConfigRow {
  portNumber: number;
  cType?: CartridgeType;
  capacity?: number | null;
}

/** Editable fill level of one port */
interface FillLevelRow {
  portNumber: number;
  level?: number | null;
  /** Fill level reported by the fuel cell, reference for changes */
  currentLevel?: number;
}

/**
 * Cartridges of the fuel cell: cartridge configuration (DE type 142), fill level
 * per cartridge and active cartridge (DE type 138). The inputs are initialised
 * with the current data. The active cartridge is marked; with a fuel manager
 * another cartridge is selected by clicking it and activated together with the
 * fill levels on save. After a command the data is read again; the section stays
 * locked and greyed until this succeeds, if necessary by a manual refresh. As long as
 * the fuel cell has reported neither the sent values nor newer data, the user is
 * asked to refresh later.
 * The setting of the external fuel sensor is a sub-section with its own data and refresh.
 */
@Component({
  selector: 'avs-fuelcell-cartridge-settings',
  templateUrl: './fuelcell-cartridge-settings.component.html',
  styleUrls: ['./fuelcell-cartridge-settings.component.css']
})
export class FuelcellCartridgeSettingsComponent implements OnChanges {
  @Input() target?: FuelcellChannelAddress;

  public readonly cartridgeTypes = cartridgeTypeLabels;
  public readonly maxCapacity: number = MAX_CAPACITY;
  protected readonly CartridgeType = CartridgeType;

  public data?: FuelcellCartridgeData;
  /** Commands are only sent once the current data has been read */
  public isLoaded: boolean = false;
  /** Editing stays locked from sending a command until the next successful read */
  public isBusy: boolean = false;
  /** Notice while sending and until the successful read */
  public busyMessage: string = '';
  public information: string = '';
  /** Open after a command until the fuel cell reports the sent values or newer data */
  private pendingExpectation?: FuelcellExpectation<FuelcellCartridgeData>;
  public ports: number[] = [];

  public fillRows: FillLevelRow[] = [];
  public configRows: CartridgeConfigRow[] = [];
  public isConfigChanged: boolean = false;
  /** The configuration inputs are collapsed by default */
  public isConfigExpanded: boolean = false;
  /** Cartridge selected for activation; initialised with the reported active cartridge */
  public selectedActivePort?: number;
  /** Active cartridge reported by the fuel cell or last sent, reference for changes */
  private currentActivePort?: number;

  constructor(
    private fuelcellService: FuelcellService,
    private alertService: AlertService
  ) { }

  /** Another fuel cell: an open expectation does not apply to it */
  public ngOnChanges(): void {
    this.pendingExpectation = undefined;
    this.load();
  }

  public get isEditable(): boolean {
    return this.isLoaded && !this.isBusy;
  }

  /**
   * The previous data stays visible (greyed) until the new data is read; a successful read
   * releases editing. An open expectation after a command stays, with its notice, until
   * the fuel cell reports the sent values or newer data (by the timestamp of the fuel cell).
   */
  public load(): void {
    this.isLoaded = false;
    if (!this.target) {
      this.pendingExpectation = undefined;
      this.information = '';
      this.data = undefined;
      this.initInputs();
      this.release();
      return;
    }

    this.fuelcellService.getCartridgeData(this.target).subscribe({
      next: data => {
        this.data = data;
        this.initInputs();
        this.release();
        this.isLoaded = true;
        this.resolveExpectation(data);
      },
      // The error message itself is already shown by the HttpService.
      error: () => {
        this.data = undefined;
        this.initInputs();
        this.failRead('Die Patronendaten konnten nicht gelesen werden.');
      }
    });
  }

  private resolveExpectation(data: FuelcellCartridgeData): void {
    if (this.pendingExpectation && !this.pendingExpectation.isResolved(data)) {
      this.information = 'Die Brennstoffzelle hat die neuen Werte noch nicht gemeldet. Bitte später aktualisieren.';
      return;
    }
    this.pendingExpectation = undefined;
    this.information = '';
  }

  public getPortString(portNumber: number): string {
    return portNumber === 0 ? 'Patrone' : 'Patrone ' + portNumber;
  }

  /** Reported configuration per port for the configuration row, e.g. "1: M10, 2: Benutzerdefiniert 12 l" */
  public get configSummary(): string {
    const cartridgesConfig = this.data?.cartridgesConfig;
    if (!cartridgesConfig || cartridgesConfig.length < 1) { return 'k.A.'; }
    return [...cartridgesConfig]
      .sort((a, b) => a.portNumber - b.portNumber)
      .map(port => {
        const type = cartridgeTypeLabels.find(label => label.type === port.cartridgeConfig.cType)?.label ?? 'k.A.';
        const capacity = port.cartridgeConfig.cType === CartridgeType.Custom && port.cartridgeConfig.capacity != null
          ? ' ' + port.cartridgeConfig.capacity + ' l' : '';
        return (port.portNumber === 0 ? '' : port.portNumber + ': ') + type + capacity;
      })
      .join(', ');
  }

  public getCurrentFillLevel(portNumber?: number, data: FuelcellCartridgeData | undefined = this.data): number | undefined {
    const level = data?.fillLevels?.find(fillLevel => fillLevel.portNumber === portNumber)?.level;
    return level ?? undefined;
  }

  /** A cartridge can only be activated with a fuel manager (more than one cartridge) */
  public get isActivationPossible(): boolean {
    return this.ports.length > 1;
  }

  /** Without a reported configuration the device does not support the cartridge configuration */
  public get isConfigAvailable(): boolean {
    return this.data?.cartridgesConfig != null;
  }

  /** Only after reading it is certain that the device does not deliver a cartridge configuration */
  public get isConfigUnavailable(): boolean {
    return this.isLoaded && !this.isConfigAvailable;
  }

  public get isConfigEditable(): boolean {
    return this.isEditable && this.isConfigAvailable;
  }

  public isSelectedPort(portNumber: number): boolean {
    return this.selectedActivePort === portNumber;
  }

  /** Any cartridge other than the selected one can be selected for activation by clicking it */
  public isPortSelectable(portNumber: number): boolean {
    return this.isEditable && this.isActivationPossible && !this.isSelectedPort(portNumber);
  }

  public get isActivationChanged(): boolean {
    return this.isActivationPossible && this.selectedActivePort !== undefined
      && this.selectedActivePort !== this.currentActivePort;
  }

  public isFillLevelValid(row: FillLevelRow): boolean {
    return row.level != null && Number.isInteger(row.level) && row.level >= 0 && row.level <= 100;
  }

  public isFillLevelChanged(row: FillLevelRow): boolean {
    return row.level !== row.currentLevel;
  }

  public get changedFillRows(): FillLevelRow[] {
    return this.fillRows.filter(row => this.isFillLevelChanged(row));
  }

  /** Only changed fill levels and a changed activation are sent; the fill levels have to be valid */
  public get isFillLevelsSendable(): boolean {
    const changedRows = this.changedFillRows;
    return (changedRows.length > 0 || this.isActivationChanged) && changedRows.every(row => this.isFillLevelValid(row));
  }

  public onConfigChange(row: CartridgeConfigRow): void {
    if (row.cType !== CartridgeType.Custom) {
      row.capacity = null;
    }
    this.isConfigChanged = true;
  }

  public isCapacityValid(row: CartridgeConfigRow): boolean {
    if (row.cType !== CartridgeType.Custom) { return true; }
    return row.capacity != null && Number.isInteger(row.capacity) && row.capacity > 0 && row.capacity <= MAX_CAPACITY;
  }

  /** Only ports with a selected type are sent; all of them have to be valid */
  public get isConfigValid(): boolean {
    const configured = this.configRows.filter(row => row.cType !== undefined);
    return configured.length > 0 && configured.every(row => this.isCapacityValid(row));
  }

  /**
   * All changed fill levels are sent in one command, a changed activation as a further
   * command. Afterwards the data is read again.
   */
  public onSaveFillLevelsClicked(): void {
    if (!this.target || !this.isEditable || !this.isFillLevelsSendable) { return; }
    const target = this.target;
    const commands: Observable<any>[] = [];
    const portLevels: FuelcellPortLevel[] = this.changedFillRows
      .map(row => ({ portNumber: row.portNumber, level: row.level! }));
    const activePort = this.isActivationChanged ? this.selectedActivePort : undefined;
    if (portLevels.length > 0) {
      commands.push(this.fuelcellService.changeFillLevels(target, portLevels));
    }
    if (activePort !== undefined) {
      commands.push(this.fuelcellService.activatePort(target, activePort));
    }

    this.send(commands, 'Die Füllstände wurden an die Brennstoffzelle gesendet', data =>
      portLevels.every(portLevel => this.getCurrentFillLevel(portLevel.portNumber, data) === portLevel.level)
      && (activePort === undefined || data.activePort === activePort));
  }

  /** Selects the cartridge to activate; nothing is sent before save */
  public onPortClicked(portNumber: number): void {
    if (!this.isPortSelectable(portNumber)) { return; }
    this.selectedActivePort = portNumber;
  }

  public onSaveConfigClicked(): void {
    if (!this.target || !this.isConfigEditable || !this.isConfigValid) { return; }
    const config: CartridgeConfigPort[] = this.configRows
      .filter(row => row.cType !== undefined)
      .map(row => ({
        portNumber: row.portNumber,
        cartridgeConfig: { cType: row.cType!, capacity: row.cType === CartridgeType.Custom ? row.capacity : null }
      }));
    this.send([this.fuelcellService.changeCartridgeConfig(this.target, config)],
      'Die Patronenkonfiguration wurde an die Brennstoffzelle gesendet', data =>
      config.every(port => {
        const reported = data.cartridgesConfig?.find(item => item.portNumber === port.portNumber)?.cartridgeConfig;
        return reported?.cType === port.cartridgeConfig.cType
          && (reported?.capacity ?? null) === (port.cartridgeConfig.capacity ?? null);
      }));
  }

  /**
   * Commands: lock editing until the data is read again successfully. The expectation is
   * resolved by a newer timestamp of either the fill levels or the cartridge configuration.
   */
  private send(commands: Observable<any>[], successMessage: string,
    isExpected: (data: FuelcellCartridgeData) => boolean): void {
    this.isBusy = true;
    this.busyMessage = 'Befehl wird gesendet …';
    this.information = '';
    const expectation = new FuelcellExpectation(isExpected, data => [data.time, data.cartridgesConfigTime], this.data);
    forkJoin(commands).subscribe({
      next: () => {
        this.alertService.alert(successMessage, AlertTypes.info);
        this.pendingExpectation = expectation;
        this.busyMessage = 'Befehl gesendet, Daten werden neu gelesen …';
        this.load();
      },
      // After a failed command the current data is read again (the error itself is shown by the HttpService)
      error: () => this.load()
    });
  }

  private release(): void {
    this.isBusy = false;
    this.busyMessage = '';
  }

  /** After a command the section stays locked until a read succeeds; otherwise the read error is shown */
  private failRead(errorMessage: string): void {
    if (this.isBusy) {
      this.busyMessage = 'Die Daten konnten nicht neu gelesen werden. Zum Freigeben der Bearbeitung bitte manuell aktualisieren.';
    } else {
      this.information = errorMessage;
    }
  }

  private initInputs(): void {
    this.ports = this.getPorts();
    this.fillRows = this.ports.map(portNumber => {
      const currentLevel = this.getCurrentFillLevel(portNumber);
      return { portNumber: portNumber, level: currentLevel, currentLevel: currentLevel };
    });
    const activePort = this.data?.activePort ?? undefined;
    this.currentActivePort = activePort !== undefined && this.ports.includes(activePort) ? activePort : undefined;
    this.selectedActivePort = this.currentActivePort;
    const cartridgesConfig = this.data?.cartridgesConfig;
    this.configRows = cartridgesConfig == null ? [] : this.ports.map(portNumber => {
      const config = cartridgesConfig.find(port => port.portNumber === portNumber)?.cartridgeConfig;
      const isTypeKnown = config != null && cartridgeTypeLabels.some(label => label.type === config.cType);
      return {
        portNumber: portNumber,
        cType: isTypeKnown ? config!.cType : undefined,
        capacity: config?.capacity ?? null
      };
    });
    this.isConfigChanged = false;
  }

  /** Ports according to the fill levels and the configuration of the fuel cell */
  private getPorts(): number[] {
    if (!this.data) { return []; }
    const ports = new Set<number>([
      ...(this.data.fillLevels ?? []).map(fillLevel => fillLevel.portNumber),
      ...(this.data.cartridgesConfig ?? []).map(config => config.portNumber)
    ]);
    return ports.size > 0 ? [...ports].sort((a, b) => a - b) : [...FALLBACK_PORTS];
  }
}
